import { useEffect, useState } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import { Grid3x3 } from 'lucide-react';
import { useTournament } from '@/context/TournamentContext';

import { API_BASE_URL } from '@/config/api';
const API = API_BASE_URL;

export default function BracketView() {
  const [user, setUser] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentTournament, selectedTournamentId } = useTournament();

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedTournamentId) return;

      try {
        const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(userRes.data);

        const bracketRes = await axios.get(
          `${API}/bracket?tournament_id=${selectedTournamentId}`
        );
        setTeams(bracketRes.data.teams);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedTournamentId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-600 border-t-transparent"></div>
      </div>
    );
  }

  const regions = ['East', 'West', 'South', 'Midwest'];
  const teamsByRegion = {};
  regions.forEach((region) => {
    teamsByRegion[region] = teams
      .filter((t) => t.region === region)
      .sort((a, b) => a.seed - b.seed);
  });

  return (
    <div className="min-h-screen bg-stone-50">
      <Navigation user={user} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-emerald-900 rounded-md p-8 mb-8 card-shadow text-white">
          <div className="flex items-center space-x-3">
            <Grid3x3 size={48} className="text-amber-600" />
            <div>
              <h1 className="font-bebas text-5xl tracking-wider">Tournament Bracket</h1>
              <p className="font-manrope text-emerald-100">View all 64 teams by region</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {regions.map((region) => (
            <div key={region} className="bg-white rounded-md card-shadow p-6">
              <h2 className="font-bebas text-3xl text-emerald-900 mb-4 tracking-wider border-b-2 border-amber-600 pb-2">
                {region}
              </h2>
              <div className="space-y-2">
                {teamsByRegion[region]?.map((team) => (
                  <div
                    key={team.team_id}
                    data-testid={`bracket-team-${team.team_id}`}
                    className={`flex items-center justify-between p-3 rounded-md ${
                      team.eliminated
                        ? 'bg-gray-200 text-gray-500 line-through'
                        : 'bg-emerald-50 hover:bg-emerald-100'
                    } transition-colors`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="font-bebas text-2xl text-amber-600 w-8">
                        {team.seed}
                      </span>
                      <span className="font-manrope font-bold text-emerald-900">
                        {team.name}
                      </span>
                    </div>
                    {team.eliminated && (
                      <span className="text-xs font-manrope text-red-600 font-bold">
                        ELIMINATED
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}